import { isTimeSeriesData, PrometheusQuerySpec, replaceTemplateVariables, TimeSeries } from '../../../core';
import { DataQueryPlugin } from '../../../plugin-system';
import { getFormattedPrometheusSeriesName } from '../../utils';
import { getData } from './get-data';

export const OffsetHeaderName = 'Chrono-Offset-Query';
export const SeriesIdLabel = '__chrono_series__';
export const OffsetLabel = '__chrono_offset__';

/*
  This wraps an existing getData fn to append the chrono-offset-query header and handle tagging
  the compared series in the response
*/
export const chronoOffsetGetDataWrapper: (
  getDataFn: typeof getData
) => NonNullable<DataQueryPlugin<PrometheusQuerySpec>['getComparableData']> = (getDataFn) => {
  return async (spec, ctx) => {
    const headers: Record<string, string> = {};

    if (
      ctx.compareDurationInMs !== undefined &&
      ctx.compareDurationInMs !== null &&
      ctx.compareDurationInMs > 0 &&
      ctx.dataType === 'TimeSeries' // compare mode is handled in the getData function for data types that use instant queries
    ) {
      headers[OffsetHeaderName] = `${ctx.compareDurationInMs}ms`;
    }

    const result = await getDataFn(spec, ctx, headers);

    if (isTimeSeriesData(result.data)) {
      let seriesNameFormat = spec.series_name_format;
      // if series name format is defined, replace template variable placeholders in series name format
      if (seriesNameFormat) {
        seriesNameFormat = replaceTemplateVariables(seriesNameFormat, ctx.variableState);
      }

      result.data.series = tagComparableSeries(
        result.data.series,
        { ...spec, series_name_format: seriesNameFormat },
        ctx.alias
      );
    }

    return result;
  };
};

/*
 Data that comes back when using the Chrono-Offset-Query header will have additional labels of
 "__chrono_series__" which is used to relate compareable series, and "__chrono_offset__" on the offset series
 that is a descriptor for the offset

 This function removes those labels, renames the series, and sets appropriate meta data for offset series for the series
 that it is compared to
*/
export const tagComparableSeries = (series: TimeSeries[], spec: PrometheusQuerySpec, alias?: string) => {
  // Build a map of series id => index of the non offset series
  const seriesIdMap: Record<string, number> = {};

  series.forEach((series, index) => {
    if (
      series.labels !== undefined &&
      series.labels[SeriesIdLabel] !== undefined &&
      series.labels[OffsetLabel] === undefined
    ) {
      seriesIdMap[series.labels[SeriesIdLabel]] = index;
    }
  });

  return series.map((series) => {
    const labels = series.labels !== undefined ? { ...series.labels } : {};

    const chronoOffset = labels[OffsetLabel];
    const chronoSeries = labels[SeriesIdLabel];
    delete labels[SeriesIdLabel];
    delete labels[OffsetLabel];

    let newName = getFormattedPrometheusSeriesName({
      alias,
      query: spec.query,
      seriesName: spec.series_name,
      seriesNameFormat: spec.series_name_format,
      metric: labels,
    });
    const config = series.config || {};

    if (chronoOffset !== undefined) {
      const seriesIndex = chronoSeries !== undefined ? seriesIdMap[chronoSeries] : undefined;

      config.compareTo = seriesIndex === undefined ? null : seriesIndex;

      if (labels.__name__ !== undefined) {
        labels.__name__ = `${labels.__name__}-${chronoOffset}`;
        newName = getFormattedPrometheusSeriesName({
          alias,
          query: spec.query,
          seriesName: spec.series_name,
          seriesNameFormat: spec.series_name_format,
          metric: labels,
        });
      } else {
        newName.formattedName = `${newName.formattedName}-${chronoOffset}`;
        newName.name = `${newName.name}-${chronoOffset}`;
      }
    }

    return {
      ...series,
      labels,
      ...newName,
      config,
    };
  });
};
